import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { ContentContext } from "../contexts/ContentContext";
import { FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import BrandMark from "./BrandMark";
import SocialIcons from "./SocialIcons";
import NewsletterSignup from "./NewsletterSignup";

export default function Footer() {
    const { data } = useContext(ContentContext);
    const year = new Date().getFullYear();

    return (
        <footer className="relative gradient-navy text-white/80 mt-24 overflow-hidden">
            <div className="absolute inset-0 brand-pattern-light opacity-25 pointer-events-none" />

            {/* Brand values strip — synced with About page values */}
            <div className="relative border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
                    {(data.about?.values?.items || []).map((v, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm font-semibold tracking-wide">
                            <span className={`w-1.5 h-1.5 rounded-full ${i === 3 ? "bg-gold-400" : "bg-brand-leaf"}`} />
                            <span className={i === 3 ? "text-gold-300" : "text-white/85"}>{v.title}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-2">
                        <div className="flex items-center gap-3 mb-4">
                            <BrandMark className="h-12 w-12" />
                            <div className="leading-tight">
                                <div className="font-serif text-xl font-bold text-white">
                                    {data.brand.name}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-gold-300 font-semibold mt-0.5">
                                    {data.brand.shortTagline}
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-white/75 leading-relaxed mb-5">
                            {data.footer.about}
                        </p>
                        <SocialIcons tone="light" />
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                            {data.footer.quickLinks}
                        </h4>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/" className="hover:text-gold-300 transition">{data.header.home}</Link></li>
                            <li><Link to="/about" className="hover:text-gold-300 transition">{data.header.about}</Link></li>
                            <li><Link to="/products" className="hover:text-gold-300 transition">{data.header.products}</Link></li>
                            <li><Link to="/sectors" className="hover:text-gold-300 transition">{data.header.sectors}</Link></li>
                            <li><Link to="/partners" className="hover:text-gold-300 transition">{data.header.partners}</Link></li>
                            <li><Link to="/contact" className="hover:text-gold-300 transition">{data.header.contact}</Link></li>
                            <li><Link to="/faq" className="hover:text-gold-300 transition">FAQ</Link></li>
                            <li><Link to="/careers" className="hover:text-gold-300 transition">Careers</Link></li>
                            <li><Link to="/press" className="hover:text-gold-300 transition">Press</Link></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                            {data.footer.getInTouch}
                        </h4>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-3">
                                <FiMail className="w-4 h-4 mt-0.5 text-gold-300 shrink-0" />
                                <a href={`mailto:${data.contact.email}`} className="hover:text-gold-300 transition break-all">
                                    {data.contact.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <FiPhone className="w-4 h-4 mt-0.5 text-gold-300 shrink-0" />
                                <a href={`tel:${data.contact.phoneE164 || data.contact.phone.replace(/\s/g, "")}`} className="hover:text-gold-300 transition">
                                    {data.contact.phone}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <FiMapPin className="w-4 h-4 mt-0.5 text-gold-300 shrink-0" />
                                <span>{data.contact.address}</span>
                            </li>
                            {data.contact.hours && (
                                <li className="text-white/70">
                                    {data.contact.hours}
                                </li>
                            )}
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                            {data.newsletter?.title || "Stay in touch"}
                        </h4>
                        <p className="text-xs text-white/70 mb-3 leading-relaxed">
                            {data.newsletter?.subtitle}
                        </p>
                        <NewsletterSignup compact />
                    </div>
                </div>

                {/* Legal / corporate line */}
                {data.legal?.footerLine && (
                    <div className="mt-12 pt-6 border-t border-white/10 text-xs text-white/55 leading-relaxed">
                        {data.legal.footerLine}
                    </div>
                )}

                <div className="mt-6 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/65">
                    <div>© {year} {data.brand.name}. {data.footer.rights}</div>
                    <div className="flex items-center gap-5 text-xs">
                        <Link to="/privacy" className="hover:text-gold-300 transition">{data.footer.privacy || "Privacy"}</Link>
                        <Link to="/terms" className="hover:text-gold-300 transition">{data.footer.terms || "Terms"}</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
